import { useCallback, useEffect, useMemo, useState } from 'react';
import { StyledTable } from './StyledTable';
import { buildSortableColumns } from './utils/sort/buildSortableColumns';
import { getExpandable } from './utils/getExpandable';
import classNames from 'classnames';
import { useInterstateTheme } from '../InterstateThemeProvider';
import { StyledPagination } from './StyledPagination';
import { styledTableContainer } from './StyledTableContainer';
import { ConfigProvider } from 'antd';
import { getFilteredData } from './utils/getFilteredData';
import { RowSelectCell } from './RowSelectCell';
import { RowSelectHeaderCell } from './RowSelectHeaderCell';
import { isSelectedRowDisabled } from './utils/isSelectedRowDisabled';
import { StyledFilter } from './StyledFilter';
import { Interstitial } from '../Interstitial';
import { StyledVirtualTable } from './StyledVirtualTable';
import memoize from 'fast-memoize';
import { idBuilder } from '../internal';
import { Box } from '../Box';
import { stringify } from 'json-cycle';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const createOnClickArgs = (expanded, key, expandAllControl) => ({
  expanded,
  key,
  expandAllControl
});
export const Table = props => {
  const theme = useInterstateTheme();
  const {
    borderType = 'none',
    columns,
    data,
    expandedRowComponent,
    expandedRows,
    onExpandClick,
    onChangeSorter,
    onChangePage,
    onRowCallback,
    rowClassName,
    scrollX,
    scrollY,
    sortableColumns,
    paginationDefaultCurrent = 1,
    paginationCurrent,
    currentPageSize,
    onChangePageSize,
    defaultExpandedRowKeys = [],
    dataDensity = 'small',
    displayPageSizeSelector = false,
    displayFilter = false,
    displayHeader = true,
    expandAllControl = 'uncontrolled',
    headerBackgroundColor = 'white',
    highlightOnHover = true,
    isLoading = false,
    emptyText = 'No Data To Display',
    enablePagination = true,
    expandRowOnClick = true,
    defaultPageSize = 10,
    paginationPageSizeOptions = [10, 20, 50, 100],
    rowExpandable = () => true,
    sortableDetails,
    id,
    tableLayout = 'fixed',
    enableRowSelection,
    selectedRowKeys,
    onChangeSelectedRows,
    onRowSelect,
    disabledSelectedRowKeys,
    filterInputPlaceholder,
    pageSizePosition = 'top',
    virtual,
    ...rest
  } = props;
  if (process.env.NODE_ENV !== 'production' && expandedRows && defaultExpandedRowKeys && expandedRows.length > 0 && defaultExpandedRowKeys.length > 0) {
    // eslint-disable-next-line no-console
    console.warn('The expandedRows and defaultExpandedRowKeys were both provided.  Only one of them should be provided.  If both are provided then the expandedRows property will be used and the defaultExpandedRowKeys property will be ignored');
  }
  if (process.env.NODE_ENV !== 'production' && virtual && (!scrollX || typeof scrollX === 'string' || typeof scrollY === 'string' || scrollX && scrollX <= 0 || !scrollY || scrollY && scrollY <= 0)) {
    // eslint-disable-next-line no-console
    console.warn("the virtual property is set to true but the scrollX or scrollY don't have values or the values are <=0 or the values are not numeric");
  }
  // The current expanded keys
  const [_expandedKeys, setExpandedKeys] = useState(defaultExpandedRowKeys);

  // The value that was entered in the filter text input
  const [_filterValue, setFilterValue] = useState('');

  // set the initial page size to the default page if pagination is enabled otherwise set it to the data length
  const [_pageSizeState, setPageSizeState] = useState(enablePagination && defaultPageSize ? defaultPageSize : (data || []).length);

  // initialize the current page with the default current
  const [_currentPage, setCurrentPage] = useState(enablePagination && paginationDefaultCurrent ? paginationDefaultCurrent : 1);

  // Used to determine if the table is currently scrolling, used to know when to draw horizontal shadow
  const [_isScrolling, setIsScrolling] = useState(false);

  // if using selected rows then this is the current list of selected rows
  const [_selectedRowKeysState, setSelectedRowKeysState] = useState(selectedRowKeys || []);
  useEffect(() => {
    setSelectedRowKeysState(selectedRowKeys ? selectedRowKeys.slice(0) : []);
  }, [selectedRowKeys]);
  // this is used to keep track of the current state of the selectRowHeader
  const [_selectedRowHeaderState, setSelectedRowHeaderState] = useState({
    checked: false,
    indeterminate: false
  });

  // keep current page updated with paginationCurrent property if pagination is enabled
  useEffect(() => {
    if (enablePagination) {
      if (paginationCurrent) {
        setCurrentPage(paginationCurrent);
      }
    } else {
      setCurrentPage(1);
    }
  }, [enablePagination, paginationCurrent]);

  // keep current page size update with currentPageSize if pagination is enabled
  useEffect(() => {
    if (enablePagination) {
      if (currentPageSize) {
        setPageSizeState(currentPageSize);
      }
    } else {
      setPageSizeState((data || []).length);
    }
  }, [enablePagination, currentPageSize]);

  // used to manage whether or not to display the horizontal shadow
  const onScrollBody = useCallback(e => {
    if (scrollY && e?.target.scrollTop > 0) {
      setIsScrolling(true);
    } else {
      setIsScrolling(false);
    }
  }, []);

  // Listener for monitoring scroll state
  useEffect(() => {
    const parent = document.getElementById(effectiveId);
    if (parent) {
      const scrollable = parent.querySelector('.ant-table-body') || parent.querySelector('.ant-table-tbody-virtual-holder'); // '.rc-virtual-list-holder'
      if (scrollable) {
        scrollable.addEventListener('scroll', onScrollBody);
        return () => {
          scrollable.removeEventListener('scroll', onScrollBody);
        };
      }
    }
  }, []);
  useEffect(() => {
    if (expandAllControl === 'expanded') {
      setExpandedKeys(data.map(each => each.key));
      if (onExpandClick) {
        data.forEach(d => onExpandClick(createOnClickArgs(true, d.key, expandAllControl)));
      }
    } else if (expandAllControl === 'closed') {
      setExpandedKeys([]);
      if (onExpandClick) {
        data.forEach(d => onExpandClick(createOnClickArgs(false, d.key, expandAllControl)));
      }
    }
  }, [expandAllControl, data, onExpandClick]);

  // when the page changes update the current page, this will get new slice
  // the new page number is passed back to the table in the pagination object
  const handlePageChange = useCallback(pageNumber => {
    setCurrentPage(pageNumber);
    if (onChangePage) {
      onChangePage(pageNumber);
    }
  }, []);

  // callback for the expand action
  const onExpand = useCallback((expanded, _ref) => {
    let {
      key
    } = _ref;
    if (onExpandClick) {
      onExpandClick(createOnClickArgs(expanded, key, expandAllControl));
    }
    if (expandAllControl === 'uncontrolled') {
      setExpandedKeys(expanded ? _expandedKeys.concat(key) : _expandedKeys.filter(k => k !== key));
    }
  }, [onExpandClick, _expandedKeys, expandAllControl]);

  // Used to update the filter value
  const updateFilterValue = useCallback(e => {
    const {
      value
    } = e.target;
    setFilterValue(value);
  }, [setFilterValue]);
  const effectiveId = id || 'interstate-table';
  const effectiveDataTestId = rest['data-testid'] || 'interstate-table-data-testid';

  // Pagination page change come from the StyledPagination component
  // TODO: PageSize selector is turned off until the style of it is defined
  // TODO: Integrate new page size selector with pagination events
  /**
   * Pagination Event handling
   * When a new page is selected from pagination component
   *    Should fire:
   *       onChangePage
   * When a new page size is selected
   *    Should fire:
   *       onChangePageSize
   */
  const buildPagination = useMemo(() => {
    return enablePagination ? {
      current: _currentPage,
      pageSize: _pageSizeState,
      itemRender: () => {
        return null;
      },
      defaultPageSize,
      defaultCurrent: paginationDefaultCurrent,
      pageSizeOptions: paginationPageSizeOptions
    } : false;
  }, [_currentPage, enablePagination, _pageSizeState]);

  /* ----------------------------- Row Selection ----------------------*/
  // handle when a row select checkbox is clicked
  const handleOnRowSelect = useCallback(e => {
    if (onRowSelect && e.target?.value) {
      onRowSelect(e.target.value.checked, e.target.value.checkboxValue);
    }
  }, []);
  // handle when the row select header cell is clicked
  const handleOnRowSelectHeader = useCallback(e => {
    if (e.target.value.checked) {
      // add all the keys except for the disabled keys
      const newKeys = data.map(r => !isSelectedRowDisabled(r.key, disabledSelectedRowKeys || []) ? r.key : undefined).filter(k => Boolean(k) || k === 0);
      setSelectedRowKeysState(newKeys);
      if (onChangeSelectedRows) {
        onChangeSelectedRows(newKeys, data);
      }
    } else {
      setSelectedRowKeysState([]);
      if (onChangeSelectedRows) {
        onChangeSelectedRows([], []);
      }
    }
  }, []);

  /**
   * Renders the select checkbox for a row
   * @param handler
   */
  const renderRowSelectCell = handler =>
  // eslint-disable-next-line react/display-name, react/no-multi-comp
  (checked, record, index, originNode) => {
    return /*#__PURE__*/_jsx(RowSelectCell, {
      onChange: handler,
      checked: checked,
      index: index,
      onChangeNative: originNode.props.onChange,
      disabled: originNode.props.disabled
    });
  };
  /**
   * Handler for the onChange when a row checkbox is selected
   * @param keys
   * @param info
   */
  const handleRowSelectOnChange = (keys, info) => {
    if (onChangeSelectedRows) {
      onChangeSelectedRows(keys, info);
    }
    setSelectedRowKeysState(keys);
  };

  /**
   * Sets the row header state, check as checked, unchecked, indeterminate
   */
  useEffect(() => {
    setSelectedRowHeaderState({
      checked: _selectedRowKeysState.length > 0,
      indeterminate: _selectedRowKeysState.length < data?.length
    });
  }, [data, _selectedRowKeysState.length]);

  /**
   * These handle the Page Size Change.
   */
  const handleOnPageSizeChange = selectedPageSize => {
    setPageSizeState(selectedPageSize);
    if (onChangePageSize) {
      onChangePageSize(selectedPageSize);
    }
  };

  /**
   * These are the current row selection parameters that Ant Table will use
   */
  const currentRowSelection = enableRowSelection ? {
    columnTitle: /*#__PURE__*/_jsx(RowSelectHeaderCell, {
      onChange: handleOnRowSelectHeader,
      checked: _selectedRowHeaderState.checked,
      indeterminate: _selectedRowHeaderState.indeterminate
    }),
    renderCell: renderRowSelectCell(handleOnRowSelect),
    columnWidth: 48,
    onChange: handleRowSelectOnChange,
    selectedRowKeys: _selectedRowKeysState,
    fixed: true,
    getCheckboxProps: record => ({
      disabled: isSelectedRowDisabled(record.key, disabledSelectedRowKeys || [])
    })
  } : undefined;
  // The onChange from the table is converted to a specific onChange function
  // The only one that we need to support is the onChangeSorter because pagination event is handled by the Pagination component
  const handleOnChange = useCallback((pagination, filters, sorter, extra) => {
    if (extra?.action === 'sort' && onChangeSorter) {
      onChangeSorter(sorter);
    }
  }, []);
  /* -------------------------- End Row Select --------------------------*/
  const resolveHeadingColor = useMemo(() => {
    const map = {
      white: {
        text: theme.tokens.SemColorOnSurfaceMuted,
        bg: theme.tokens.SemColorSurfaceDefault
      },
      light: {
        text: theme.tokens.SemColorOnSurfaceMuted,
        bg: theme.tokens.SemColorSurfaceSubdued
      },
      dark: {
        text: theme.tokens.SemColorOnSurfaceInverse,
        bg: theme.tokens.SemColorSurfaceInverseDefault
      }
    };
    return {
      text: map[headerBackgroundColor || 'white'].text,
      bg: map[headerBackgroundColor || 'white'].bg
    };
  }, [theme, headerBackgroundColor]);
  const resolveAntTheme = memoize(theme => ({
    token: {
      fontFamily: theme.tokens.SemTypeFontBody
    },
    components: {
      Table: {
        headerColor: resolveHeadingColor.text,
        headerBg: resolveHeadingColor.bg,
        headerSortHoverBg: resolveHeadingColor.bg,
        headerSortActiveBg: resolveHeadingColor.bg,
        selectionColumnWidth: 20,
        borderColor: theme.tokens.SemColorBorderMuted,
        headerBorderRadius: 0,
        rowHoverBg: theme.tokens.SemColorSurfaceInteractiveHover,
        rowExpanded: theme.tokens.SemColorSurfaceInteractiveHover,
        rowSelectedBg: theme.tokens.SemColorSurfaceDefault,
        rowSelectedHoverBg: theme.tokens.SemColorSurfaceDefault
      }
    }
  }), {
    serializer: stringify
  });
  const buildColumns = memoize(buildSortableColumns, {
    serializer: stringify
  });
  const filteredData = memoize(getFilteredData, {
    serializer: stringify
  });
  return /*#__PURE__*/_jsxs(Box, {
    sx: styledTableContainer(),
    className: classNames(['interstate-ant-table-wrapper', !highlightOnHover && 'interstate-ant-table__no-highlight--hover', headerBackgroundColor]),
    id: idBuilder('ids-table', effectiveId),
    "data-testid": idBuilder('ids-table', effectiveDataTestId),
    children: [isLoading && /*#__PURE__*/_jsx(Interstitial, {}), /*#__PURE__*/_jsx(StyledFilter, {
      setFilterData: updateFilterValue,
      dataDensity: dataDensity,
      placeholder: filterInputPlaceholder,
      pageSize: _pageSizeState,
      displayFilter: displayFilter,
      displayPageSizeSelector: displayPageSizeSelector && pageSizePosition === 'top',
      defaultPageSize: defaultPageSize,
      paginationPageSizeOptions: paginationPageSizeOptions,
      onShowSizeChange: handleOnPageSizeChange,
      id: effectiveId,
      "data-testid": effectiveDataTestId
    }), virtual && scrollY ? /*#__PURE__*/_jsx(ConfigProvider, {
      theme: resolveAntTheme(theme),
      children: /*#__PURE__*/_jsx(StyledVirtualTable, {
        className: 'ids-styled-virtual-table',
        showSorterTooltip: false,
        dataSource: displayFilter ? filteredData(data, _filterValue, columns) : data,
        columns: buildColumns(data, columns, sortableDetails, Boolean(sortableColumns), theme, headerBackgroundColor),
        pagination: buildPagination,
        locale: {
          emptyText
        },
        rowClassName: rowClassName,
        expandable: getExpandable(expandedRowComponent, expandAllControl, rowExpandable, onExpand, expandRowOnClick && !enableRowSelection, expandedRows, _expandedKeys),
        showHeader: displayHeader,
        onRow: onRowCallback,
        onChange: handleOnChange,
        scroll: {
          x: scrollX,
          y: scrollY
        },
        highlightOnHover: highlightOnHover,
        headerBackgroundColor: headerBackgroundColor,
        id: idBuilder('ids-table-content', effectiveId),
        "data-testid": idBuilder('ids-table-content', effectiveDataTestId),
        rowSelection: enableRowSelection ? currentRowSelection : undefined,
        dataDensity: dataDensity,
        tableLayout: tableLayout,
        sticky: Boolean(scrollY),
        isScrolling: _isScrolling,
        borderType: borderType,
        virtual: true
      })
    }) : /*#__PURE__*/_jsx(StyledTable, {
      className: 'ids-styled-table',
      showSorterTooltip: false,
      dataSource: displayFilter ? filteredData(data, _filterValue, columns) : data,
      columns: buildColumns(data, columns, sortableDetails, Boolean(sortableColumns), theme, headerBackgroundColor),
      pagination: buildPagination,
      locale: {
        emptyText
      },
      rowClassName: rowClassName,
      expandable: getExpandable(expandedRowComponent, expandAllControl, rowExpandable, onExpand, expandRowOnClick && !enableRowSelection, expandedRows, _expandedKeys),
      showHeader: displayHeader,
      onRow: onRowCallback,
      onChange: handleOnChange,
      scroll: {
        x: scrollX,
        y: scrollY
      },
      highlightOnHover: highlightOnHover,
      headerBackgroundColor: headerBackgroundColor,
      id: idBuilder('ids-table-content', effectiveId),
      "data-testid": idBuilder('ids-table-content', effectiveDataTestId),
      rowSelection: enableRowSelection ? currentRowSelection : undefined,
      dataDensity: dataDensity,
      tableLayout: tableLayout,
      sticky: Boolean(scrollY),
      isScrolling: _isScrolling,
      borderType: borderType
    }), /*#__PURE__*/_jsx(StyledPagination, {
      enablePagination: enablePagination,
      pageSize: _pageSizeState,
      numberOfRows: (data || []).length,
      onChange: handlePageChange,
      page: _currentPage,
      displayPageSizeSelector: displayPageSizeSelector && pageSizePosition === 'bottom',
      defaultPageSize: defaultPageSize,
      paginationPageSizeOptions: paginationPageSizeOptions,
      onShowSizeChange: handleOnPageSizeChange,
      id: idBuilder('table', effectiveId),
      "data-testid": idBuilder('table', effectiveDataTestId)
    })]
  });
};